import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import CheckboxListValue from './CheckboxListValue';
import {
    checkboxListValueTypes,
    extendedStateType,
} from '../../types';
import './_styles_/CheckboxList.css';
import ResetFieldButton from '../buttons/ResetFieldButton';
import { joinClassNames } from '../../utils';
import { NO_STATE } from '../../hooks/useExtendedState';
import {
    resolveIsChanged,
    resolveOnReset,
    resolveValue,
} from '../../utils/fields';

CheckboxList.propTypes = {
    label: PropTypes.node.isRequired,
    name: PropTypes.string.isRequired,
    values: checkboxListValueTypes.isRequired,
    selectedValues: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
    className: PropTypes.string,
    onReset: PropTypes.func,
    warningGenerator: PropTypes.func,
    isChanged: PropTypes.bool,
    isRequired: PropTypes.bool,
    allowedCombinations: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    state: extendedStateType,
};

CheckboxList.defaultProps = {
    selectedValues: undefined,
    onChange: undefined,
    className: '',
    onReset: undefined,
    warningGenerator: null,
    isChanged: undefined,
    isRequired: false,
    allowedCombinations: null,
    state: NO_STATE,
};

function CheckboxList(props) {
    const {
        label,
        name,
        values,
        allowedCombinations,
        selectedValues,
        className,
        onChange,
        onReset,
        warningGenerator,
        isChanged,
        isRequired,
        state,
    } = props;
    const { t } = useTranslation();
    const resolvedSelectedValues = resolveValue(selectedValues, state) || [];
    const stateValueSetter = state && state !== NO_STATE ? state.setValueIfNew : () => {};
    const [displayedValue, setDisplayedValue] = useState(resolvedSelectedValues);
    useEffectOnUpdate(() => {
        const newResolvedSelectedValues = resolveValue(selectedValues, state);
        setDisplayedValue(newResolvedSelectedValues);
    }, [selectedValues, state.value]);
    const onChangeValue = (changedValueId) => (checked) => {
        let newState;
        if (checked) {
            if (displayedValue.indexOf(changedValueId) === -1) {
                newState = [...displayedValue, changedValueId];
            }
        } else {
            newState = displayedValue.filter((selectedValue) => selectedValue !== changedValueId);
        }
        setDisplayedValue(newState);
        stateValueSetter(newState);
        onChange && onChange(newState);
    };
    const warning = warningGenerator && warningGenerator();
    const isPartOfAllowedCombination = (valueId) => {
        let result = true;
        const potentialCombination = [...resolvedSelectedValues, valueId];
        if (allowedCombinations && resolvedSelectedValues.length && !resolvedSelectedValues.includes(valueId)) {
            result = allowedCombinations.some(
                (allowedCombination) => potentialCombination.every(
                    (valueFromPotentialCombination) => allowedCombination.includes(valueFromPotentialCombination),
                ),
            );
        }
        return result;
    };
    const resolvedOnReset = resolveOnReset(onReset, state);
    const resolvedIsChanged = resolveIsChanged(isChanged, state);
    return (
        <div className={joinClassNames('checkboxlist', 'field', className, resolvedIsChanged && 'modified', (isRequired && !displayedValue.length) && 'required')}>
            <div className="checkboxlist-field">
                <div className="field--key">
                    {label}
                    {warning && (
                        <div className="validation-warning">
                            {t(warning)}
                        </div>
                    )}
                </div>
                <div className="checkboxlist-values">
                    {values.map((value) => (
                        <CheckboxListValue
                            key={value.id}
                            name={name}
                            value={value}
                            checked={resolvedSelectedValues.indexOf(value.id) !== -1}
                            onChange={onChangeValue(value.id)}
                            disabled={!isPartOfAllowedCombination(value.id)}
                        />
                    ))}
                </div>
            </div>
            {resolvedIsChanged && resolvedOnReset && <ResetFieldButton onReset={resolvedOnReset} />}
        </div>
    );
}

export default CheckboxList;
